import { useState } from 'react';
import useUser from '../hooks/useUser';
import GlobalContext from '../contexts/GlobalContext';
import { Switch, BrowserRouter, Route } from 'react-router-dom';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import LandingPage from '../pages/LandingPage';
import SsoStartPage from '../pages/sso/SsoStartPage';
import SsoCallbackPage from '../pages/sso/SsoCallbackPage';
import SsoCompleteProfilePage from '../pages/sso/SsoCompleteProfilePage';
import SsoRosterPage from '../pages/sso/SsoRosterPage';
import PayrollAdminPage from '../pages/payroll/PayrollAdminPage';
import TimeClockAdminPage from '../pages/payroll/TimeClockAdminPage';
import KioskPage from '../pages/kiosk/KioskPage';
import SacrificePage from '../pages/sacrifice/SacrificePage';
import SalesItemsPage from '../pages/sales-items/SalesItemsPage';
import SalesSummaryPage from '../pages/sales-summary/SalesSummaryPage';
import DashboardPage from '../pages/DashboardPage';
import UserPage from '../pages/user/UserPage';
import FeePage from '../pages/fee/FeePage';
import TransactionLookUpPage from '../pages/transaction/TransactionLookUpPage';
import TransactionPage from '../pages/transaction/TransactionPage';
import GuestDatabasePage from '../pages/guest/GuestDatabasePage';
import GuestCheckInPage from '../pages/guest/GuestCheckInPage';
import LimitedCheckInConfirmationPage from '../pages/guest/LimitedCheckInConfirmationPage';
import UpdateGuestPage from '../pages/guest/UpdateGuestPage';
import GuestCreatePage from '../pages/guest/GuestCreatePage';
import NoticeGuestPage from '../pages/guest/NoticeGuestPage';
import TosGuestPage from '../pages/guest/TosGuestPage';
import ScanNewGuestTosPage from '../pages/guest/ScanNewGuestTosPage';
import GuestConfirmationPage from '../pages/guest/GuestConfirmationPage';
import EditGuestPage from '../pages/guest/EditGuestPage';
import Tos from '../components/Tos';
import Lease from '../components/Lease';
import SignatureGuestPage from '../pages/guest/SignatureGuestPage';
import StageNameMatchesPage from '../pages/guest/StageNameMatchesPage';
import BannedGuestPage from '../pages/banned/BannedGuestPage';
import BansListPage from '../pages/admin/BansListPage';
import BanFromScanPage from '../pages/admin/BanFromScanPage';
import Loading from '../components/Loading';
import IdleLogout from '../components/IdleLogout';
import VersionWatcher from '../components/VersionWatcher';

const AppRouter = () => {
    const { user, setUser, isLoading, setLoading } = useUser();
    const [guest, setGuest] = useState(null);

    if (isLoading) {
        return <Loading loading={isLoading} />;
    }

    return (
        <GlobalContext.Provider
            value={{ user, setUser, isLoading, setLoading, guest, setGuest }}
        >
            <BrowserRouter>
                <IdleLogout />
                <VersionWatcher />
                <Switch>
                    <Route exact path="/sso/start">
                        <SsoStartPage />
                    </Route>
                    <Route exact path="/auth/sso-callback">
                        <SsoCallbackPage />
                    </Route>
                    <Route exact path="/sso/complete-profile">
                        <SsoCompleteProfilePage />
                    </Route>
                    <PublicRoute exact path="/">
                        <LandingPage />
                    </PublicRoute>
                    <PrivateRoute exact path="/tos">
                        <Tos />
                    </PrivateRoute>
                    <PrivateRoute exact path="/lease">
                        <Lease />
                    </PrivateRoute>
                    <PrivateRoute exact path="/dashboard">
                        <DashboardPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/users">
                        <UserPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/fees">
                        <FeePage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/transactions">
                        <TransactionLookUpPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/transactions/:date">
                        <TransactionPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/editGuest/:type">
                        <EditGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests">
                        <GuestDatabasePage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/sso-roster">
                        <SsoRosterPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/payroll">
                        <PayrollAdminPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/timeclock">
                        <TimeClockAdminPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/kiosk">
                        <KioskPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/sacrifice">
                        <SacrificePage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/sales-items">
                        <SalesItemsPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/sales-summary">
                        <SalesSummaryPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/limited/confirm">
                        <LimitedCheckInConfirmationPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/:type/stage-matches">
                        <StageNameMatchesPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/scan-new/:type">
                        <ScanNewGuestTosPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/:type">
                        <GuestCheckInPage />
                    </PrivateRoute>
                    <PrivateRoute
                        exact
                        path="/guests/checkin/create/guest/:type/:random"
                    >
                        <GuestCreatePage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/notice">
                        <NoticeGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/notice/tos">
                        <TosGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/signature">
                        <SignatureGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/:type/banned">
                        <BannedGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/admin/bans">
                        <BansListPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/admin/bans/from-scan">
                        <BanFromScanPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/checkin/:type/:id/update">
                        <UpdateGuestPage />
                    </PrivateRoute>
                    <PrivateRoute exact path="/guests/confirmation/:type">
                        <GuestConfirmationPage />
                    </PrivateRoute>
                </Switch>
            </BrowserRouter>
        </GlobalContext.Provider>
    );
};

export default AppRouter;
